import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const API_URI = process.env.REACT_APP_API_URI || 'http://localhost:5000';
const CloudUrl = process.env.REACT_APP_CLOUD_FLARE_URL;

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike', 'code-block'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'image', 'video'],
    ['clean']
  ]
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike', 'code-block',
  'list', 'bullet',
  'link', 'image', 'video'
];

const AdminDashboard = () => {
  const [blogs, setBlogs] = useState([]);
  const [projects, setProjects] = useState([]);

  const [blogForm, setBlogForm] = useState({
    _id: null, title: '', description: '', content: '', images: []
  });
  const [projectForm, setProjectForm] = useState({
    _id: null, title: '', description: '', content: '', images: []
  });

  const [editingBlog, setEditingBlog] = useState(false);
  const [editingProject, setEditingProject] = useState(false);

  const [token, setToken] = useState(null);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    setToken(storedToken);
  }, []); // Empty dependency array to run once on mount

  useEffect(() => {
    if (!token) return;
    const authHeaders = { 'Authorization': `Bearer ${token}` };

    const fetchData = async () => {
      try {
        const [blogRes, projectRes] = await Promise.all([
          axios.get(`${API_URI}/api/blogs`, { headers: authHeaders }),
          axios.get(`${API_URI}/api/portfolio`, { headers: authHeaders })
        ]);
        setBlogs(blogRes.data);
        setProjects(projectRes.data);
      } catch (err) {
        console.error('Fetching error:', err);
      }
    };

    fetchData();
  }, [token]); // Dependency on token

  // == BLOGS ==
  const handleBlogChange = (e) => {
    setBlogForm({ ...blogForm, [e.target.name]: e.target.value });
  };
  const handleBlogContentChange = (val) => {
    setBlogForm({ ...blogForm, content: val });
  };
  const handleBlogImagesUpload = async (e) => {
    try {
      console.log('Selected blog files:', e.target.files); 
      const formData = new FormData();
      for (const file of e.target.files) formData.append('images', file);
      const res = await axios.post(`${API_URI}/api/upload/multiple`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      console.log('Upload response for blog images:', res.data);
      if (res.data.success && res.data.fileNames) {
        setBlogForm(prev => ({
          ...prev,
          images: [...prev.images, ...res.data.fileNames]
        }));
      }
    } catch (err) {
      console.error('Blog image upload error:', err);
    }
  };
  const saveBlog = async (e) => {
    e.preventDefault();
    console.log('Saving blog form data:', blogForm);
    const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};
    console.log('Authorization header:', authHeaders); // Verify headers
    try {

      if (editingBlog && blogForm._id) {
        const res = await axios.put(`${API_URI}/api/blogs/${blogForm._id}`, blogForm, { headers: authHeaders });
        setBlogs((prev) => prev.map((b) => (b._id === res.data._id ? res.data : b)));
      } else {
        const res = await axios.post(`${API_URI}/api/blogs`, blogForm, { headers: authHeaders });
        setBlogs((prev) => [...prev, res.data]);
      }
      resetBlogForm();
    } catch (err) {
      console.error('Blog save error:', err);
    }
  };
  const editBlog = (b) => {
    setBlogForm(b);
    setEditingBlog(true);
  };
  const deleteBlog = async (id) => {
    try {
      const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};
      await axios.delete(`${API_URI}/api/blogs/${id}`, { headers: authHeaders });
      setBlogs((prev) => prev.filter((b) => b._id !== id));
    } catch (err) {
      console.error('Delete blog error:', err);
    }
  };
  const resetBlogForm = () => {
    setBlogForm({ _id: null, title: '', description: '', content: '', images: [] });
    setEditingBlog(false);
  };

  // == PROJECTS ==
  const handleProjectChange = (e) => {
    setProjectForm({ ...projectForm, [e.target.name]: e.target.value });
  };
  const handleProjectContentChange = (val) => {
    setProjectForm({ ...projectForm, content: val });
  };
  const handleProjectImagesUpload = async (e) => {
    try {
      console.log('Selected project files:', e.target.files); 
      const formData = new FormData();
      for (const file of e.target.files) formData.append('images', file);
      const res = await axios.post(`${API_URI}/api/upload/multiple`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      console.log('Upload response for project images:', res.data);
      if (res.data.success && res.data.fileNames) {
        setProjectForm(prev => ({
          ...prev,
          images: [...prev.images, ...res.data.fileNames]
        }));
      }
    } catch (err) {
      console.error('Project image upload error:', err);
    }
  };
  const saveProject = async (e) => {
    e.preventDefault();
    console.log('Saving project form data:', projectForm);
    const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};
    console.log('Authorization header:', authHeaders); // Verify headers
    try {

      if (editingProject && projectForm._id) {
        const res = await axios.put(`${API_URI}/api/portfolio/${projectForm._id}`, projectForm, { headers: authHeaders });
        setProjects((prev) => prev.map((p) => (p._id === res.data._id ? res.data : p)));
      } else {
        const res = await axios.post(`${API_URI}/api/portfolio`, projectForm, { headers: authHeaders });
        setProjects((prev) => [...prev, res.data]);
      }
      resetProjectForm();
    } catch (err) {
      console.error('Project save error:', err);
    }
  };
  const editProject = (p) => {
    setProjectForm(p);
    setEditingProject(true);
  };
  const deleteProject = async (id) => {
    try {
      const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};
      await axios.delete(`${API_URI}/api/portfolio/${id}`, { headers: authHeaders });
      setProjects((prev) => prev.filter((p) => p._id !== id));
    } catch (err) {
      console.error('Delete project error:', err);
    }
  };
  const resetProjectForm = () => {
    setProjectForm({ _id: null, title: '', description: '', content: '', images: [] });
    setEditingProject(false);
  };

  return (
    <div className="bg-black text-white min-h-screen p-6">
      <h1 className="text-3xl mb-8">Admin Dashboard</h1>

      {/* Blog Form */}
      <div className="mb-10">
        <h2 className="text-2xl mb-4">{editingBlog ? 'Edit Blog' : 'Create Blog'}</h2>
        <form onSubmit={saveBlog} className="space-y-4">
          <input
            name="title"
            value={blogForm.title}
            onChange={handleBlogChange}
            className="w-full p-2 rounded bg-gray-800 border border-gray-600"
            placeholder="Blog Title"
            required
          />
          <input
            name="description"
            value={blogForm.description}
            onChange={handleBlogChange}
            className="w-full p-2 rounded bg-gray-800 border border-gray-600"
            placeholder="Blog Description"
            required
          />
          <input type="file" multiple onChange={handleBlogImagesUpload} className="mb-2" />
          <ReactQuill
            modules={quillModules}
            formats={quillFormats}
            value={blogForm.content}
            onChange={handleBlogContentChange}
            className="bg-white text-black"
          />
          <div className="space-x-4">
            <button className="bg-green-600 px-4 py-2 rounded">
              {editingBlog ? 'Update Blog' : 'Create Blog'}
            </button>
            {editingBlog && (
              <button
                type="button"
                onClick={resetBlogForm}
                className="bg-gray-600 px-4 py-2 rounded"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Blog List */}
      <div className="mb-10">
        <h2 className="text-2xl mb-4">All Blogs</h2>
        {blogs.map((blog) => (
          <div key={blog._id} className="bg-gray-800 p-4 mb-4 rounded space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold">{blog.title}</h3>
              {blog.images && blog.images.length > 0 && (
                <img
                  src={`${CloudUrl}/${blog.images[0]}`}
                  alt="Blog Hero"
                  className="w-16 h-16 object-cover rounded"
                />
              )}
            </div>
            <p>{blog.description}</p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => editBlog(blog)}
                className="bg-blue-500 px-3 py-1 rounded"
              >
                Edit
              </button>
              <button
                onClick={() => deleteBlog(blog._id)}
                className="bg-red-500 px-3 py-1 rounded"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Project Form */}
      <div className="mb-10">
        <h2 className="text-2xl mb-4">{editingProject ? 'Edit Project' : 'Create Project'}</h2>
        <form onSubmit={saveProject} className="space-y-4">
          <input
            name="title"
            value={projectForm.title}
            onChange={handleProjectChange}
            className="w-full p-2 rounded bg-gray-800 border border-gray-600"
            placeholder="Project Title"
            required
          />
          <input
            name="description"
            value={projectForm.description}
            onChange={handleProjectChange}
            className="w-full p-2 rounded bg-gray-800 border border-gray-600"
            placeholder="Project Description"
            required
          />
          <input type="file" multiple onChange={handleProjectImagesUpload} className="mb-2" />
          <ReactQuill
            modules={quillModules}
            formats={quillFormats}
            value={projectForm.content}
            onChange={handleProjectContentChange}
            className="bg-white text-black"
          />
          <div className="space-x-4">
            <button className="bg-green-600 px-4 py-2 rounded">
              {editingProject ? 'Update Project' : 'Create Project'}
            </button>
            {editingProject && (
              <button
                type="button"
                onClick={resetProjectForm}
                className="bg-gray-600 px-4 py-2 rounded"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Project List */}
      <div className="mb-10">
        <h2 className="text-2xl mb-4">All Projects</h2>
        {projects.map((proj) => (
          <div key={proj._id} className="bg-gray-800 p-4 mb-4 rounded space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold">{proj.title}</h3>
              {proj.images && proj.images.length > 0 && (
                <img
                  src={`${CloudUrl}/${proj.images[0]}`}
                  alt="Project Hero"
                  className="w-16 h-16 object-cover rounded"
                />
              )}
            </div>
            <p>{proj.description}</p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => editProject(proj)}
                className="bg-blue-500 px-3 py-1 rounded"
              >
                Edit
              </button>
              <button
                onClick={() => deleteProject(proj._id)}
                className="bg-red-500 px-3 py-1 rounded"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default AdminDashboard;
